/**
 * Daily AI Report — full-screen modal showing flagged items.
 * Each item has instruction input, preview/confirm flow, and snooze.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  generateDailyReport,
  getLatestDailyReport,
  dismissDailyReport,
  markDailyReportRead,
  instructReportItem,
  executeReportItem,
  snoozeReportItem,
} from '../../api/agent';
import { useAuth } from '../../context/AuthContext';

const SEVERITY_COLORS = {
  critical: 'bg-red-100 text-red-800 border-red-200',
  warning:  'bg-amber-100 text-amber-800 border-amber-200',
  info:     'bg-blue-100 text-blue-800 border-blue-200',
};

const SEVERITY_ICONS = {
  critical: '🚨',
  warning:  '⚠️',
  info:     'ℹ️',
};

const CATEGORY_LABELS = {
  job:     'Job',
  rfq:     'RFQ',
  po:      'Purchase Order',
  task:    'Task',
  vendor:  'Vendor',
  parts:   'Parts',
  general: 'General',
};

const CATEGORY_COLORS = {
  job:     'bg-violet-100 text-violet-700',
  rfq:     'bg-blue-100 text-blue-700',
  po:      'bg-purple-100 text-purple-700',
  task:    'bg-orange-100 text-orange-700',
  vendor:  'bg-teal-100 text-teal-700',
  parts:   'bg-cyan-100 text-cyan-700',
  general: 'bg-gray-100 text-gray-600',
};

export default function DailyReportModal({ open, onClose, autoGenerate = false }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  // Per-item state
  const [instructions, setInstructions] = useState({});    // itemId → text
  const [previewing, setPreviewing] = useState({});         // itemId → bool
  const [previews, setPreviews] = useState({});             // itemId → preview obj
  const [executing, setExecuting] = useState({});           // itemId → bool
  const [results, setResults] = useState({});               // itemId → result obj
  const [snoozing, setSnoozing] = useState({});             // itemId → bool
  const [snoozeDays, setSnoozeDays] = useState({});         // itemId → number

  const loadLatest = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getLatestDailyReport();
      if (data) {
        setReport(data);
        if (!data.is_read) {
          await markDailyReportRead(data.id).catch(() => {});
        }
      }
    } catch {
      setError('Failed to load report.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    if (autoGenerate) {
      handleGenerate();
    } else {
      loadLatest();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleGenerate() {
    setGenerating(true);
    setError(null);
    setReport(null);
    setPreviews({});
    setInstructions({});
    setResults({});
    try {
      const data = await generateDailyReport();
      setReport(data);
      if (data?.id && !data.is_read) {
        await markDailyReportRead(data.id).catch(() => {});
      }
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to generate report.');
    } finally {
      setGenerating(false);
    }
  }

  async function handleDismiss() {
    if (!report) return;
    try {
      await dismissDailyReport(report.id);
      setReport(null);
      onClose?.();
    } catch {
      alert('Failed to dismiss report.');
    }
  }

  async function handleInstruct(itemId) {
    const text = (instructions[itemId] || '').trim();
    if (!text) return;
    setPreviewing(p => ({ ...p, [itemId]: true }));
    try {
      const data = await instructReportItem(itemId, text);
      setPreviews(p => ({ ...p, [itemId]: data.preview }));
    } catch (e) {
      alert(e?.response?.data?.error || 'Failed to generate preview.');
    } finally {
      setPreviewing(p => ({ ...p, [itemId]: false }));
    }
  }

  async function handleExecute(itemId) {
    setExecuting(e => ({ ...e, [itemId]: true }));
    try {
      const data = await executeReportItem(itemId);
      setResults(r => ({ ...r, [itemId]: data }));
      setPreviews(p => ({ ...p, [itemId]: null }));
    } catch (e) {
      setResults(r => ({ ...r, [itemId]: { success: false, error: e?.response?.data?.error || 'Execution failed.' } }));
    } finally {
      setExecuting(e => ({ ...e, [itemId]: false }));
    }
  }

  async function handleSnooze(itemId) {
    const days = snoozeDays[itemId] || 1;
    setSnoozing(s => ({ ...s, [itemId]: true }));
    try {
      await snoozeReportItem(itemId, days);
      // Remove from visible list
      setReport(prev => ({
        ...prev,
        items: prev.items.map(i => i.id === itemId ? { ...i, is_snoozed: true, snoozed_until: 'snoozed' } : i),
      }));
    } catch {
      alert('Failed to snooze item.');
    } finally {
      setSnoozing(s => ({ ...s, [itemId]: false }));
    }
  }

  if (!open) return null;

  const visibleItems = (report?.items || []).filter(i => !i.is_snoozed);
  const criticalCount = visibleItems.filter(i => i.severity === 'critical').length;
  const warningCount = visibleItems.filter(i => i.severity === 'warning').length;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 overflow-y-auto py-8">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl mx-4 my-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">📋</span>
            <div>
              <h2 className="text-lg font-bold text-gray-900">AI Daily Report</h2>
              {report && (
                <p className="text-xs text-gray-500">
                  Generated {new Date(report.created_at).toLocaleString()}
                  {criticalCount > 0 && <span className="ml-2 text-red-600 font-medium">{criticalCount} critical</span>}
                  {warningCount > 0 && <span className="ml-2 text-amber-600 font-medium">{warningCount} warning</span>}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {generating ? 'Generating…' : '↻ Refresh Report'}
            </button>
            {report && report.status === 'ready' && (
              <button
                onClick={handleDismiss}
                className="px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200"
              >
                Dismiss
              </button>
            )}
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
          {/* Loading / generating */}
          {(loading || generating) && (
            <div className="text-center py-16">
              <div className="inline-block w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin mb-3" />
              <p className="text-sm text-gray-500">{generating ? 'AI is analyzing your operations…' : 'Loading report…'}</p>
              {generating && <p className="text-xs text-gray-400 mt-1">This usually takes 15-30 seconds</p>}
            </div>
          )}

          {/* Error */}
          {error && !loading && !generating && (
            <div className="text-center py-12">
              <p className="text-sm text-red-600 mb-3">{error}</p>
              <button onClick={handleGenerate} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700">
                Try Again
              </button>
            </div>
          )}

          {/* No report */}
          {!loading && !generating && !error && !report && (
            <div className="text-center py-16">
              <span className="text-4xl mb-3 block">📋</span>
              <p className="text-sm text-gray-500 mb-3">No report yet. Generate one to see what needs attention.</p>
              <button onClick={handleGenerate} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700">
                Generate Report
              </button>
            </div>
          )}

          {/* Failed report */}
          {report && report.status === 'failed' && (
            <div className="text-center py-12">
              <p className="text-sm text-red-600 mb-2">Report generation failed</p>
              <p className="text-xs text-gray-400 mb-3">{report.error_message}</p>
              <button onClick={handleGenerate} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700">
                Try Again
              </button>
            </div>
          )}

          {/* Report items */}
          {report && report.status === 'ready' && (
            <div className="space-y-3">
              {visibleItems.length === 0 ? (
                <div className="text-center py-12">
                  <span className="text-4xl mb-3 block">✅</span>
                  <p className="text-sm font-medium text-green-700">All clear — nothing needs attention today!</p>
                </div>
              ) : (
                visibleItems.map(item => (
                  <ReportItemCard
                    key={item.id}
                    item={item}
                    isAdmin={isAdmin}
                    instruction={instructions[item.id] || ''}
                    onInstructionChange={text => setInstructions(i => ({ ...i, [item.id]: text }))}
                    onSubmitInstruction={() => handleInstruct(item.id)}
                    isPreviewing={previewing[item.id]}
                    preview={previews[item.id]}
                    onExecute={() => handleExecute(item.id)}
                    isExecuting={executing[item.id]}
                    result={results[item.id]}
                    snoozeDaysValue={snoozeDays[item.id] || 1}
                    onSnoozeDaysChange={d => setSnoozeDays(s => ({ ...s, [item.id]: d }))}
                    onSnooze={() => handleSnooze(item.id)}
                    isSnoozing={snoozing[item.id]}
                  />
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function ReportItemCard({
  item, isAdmin,
  instruction, onInstructionChange, onSubmitInstruction, isPreviewing,
  preview, onExecute, isExecuting, result,
  snoozeDaysValue, onSnoozeDaysChange, onSnooze, isSnoozing,
}) {
  const [showInstruct, setShowInstruct] = useState(false);
  const [showSnooze, setShowSnooze] = useState(false);

  const sevColor = SEVERITY_COLORS[item.severity] || SEVERITY_COLORS.info;
  const sevIcon = SEVERITY_ICONS[item.severity] || 'ℹ️';
  const catLabel = CATEGORY_LABELS[item.category] || item.category;
  const catColor = CATEGORY_COLORS[item.category] || CATEGORY_COLORS.general;

  return (
    <div className={`rounded-lg border ${item.severity === 'critical' ? 'border-red-300 bg-red-50/30' : 'border-gray-200 bg-white'}`}>
      {/* Item header */}
      <div className="px-4 py-3">
        <div className="flex items-start gap-2">
          <span className="text-base mt-0.5">{sevIcon}</span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-0.5">
              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${catColor}`}>
                {catLabel}
              </span>
              {item.related_job_number && (
                <span className="text-xs text-gray-500">Job #{item.related_job_number}</span>
              )}
              {item.related_vendor_name && (
                <span className="text-xs text-gray-500">{item.related_vendor_name}</span>
              )}
            </div>
            <p className="text-sm font-semibold text-gray-900">{item.title}</p>
            <p className="text-sm text-gray-600 mt-0.5">{item.description}</p>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 mt-2 ml-7">
          {!result?.success && item.instruction_status !== 'completed' && (
            <button
              onClick={() => { setShowInstruct(!showInstruct); setShowSnooze(false); }}
              className={`text-xs font-medium px-2.5 py-1 rounded-md transition-colors ${
                showInstruct ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
              }`}
            >
              💬 Instruct
            </button>
          )}
          <button
            onClick={() => { setShowSnooze(!showSnooze); setShowInstruct(false); }}
            className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded-md hover:bg-gray-100"
          >
            😴 Snooze
          </button>
        </div>
      </div>

      {/* Instruction input */}
      {showInstruct && (
        <div className="px-4 py-3 border-t border-gray-100 bg-blue-50/50">
          <p className="text-xs text-gray-500 mb-1.5">Tell me how to handle this:</p>
          <div className="flex gap-2">
            <input
              type="text"
              value={instruction}
              onChange={e => onInstructionChange(e.target.value)}
              placeholder='e.g. "Resend RFQ to Streamline" or "Change vendor to Allied and re-send"'
              className="flex-1 px-3 py-1.5 text-sm border border-blue-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
              onKeyDown={e => e.key === 'Enter' && instruction.trim() && onSubmitInstruction()}
            />
            <button
              onClick={onSubmitInstruction}
              disabled={!instruction.trim() || isPreviewing}
              className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap"
            >
              {isPreviewing ? 'Planning…' : 'Preview'}
            </button>
          </div>
        </div>
      )}

      {/* Execution preview */}
      {preview && !result && (
        <div className="px-4 py-3 border-t border-gray-100 bg-amber-50/50">
          <p className="text-xs font-semibold text-amber-800 mb-1.5">🔍 Planned actions:</p>
          <p className="text-sm text-gray-700 mb-2">{preview.summary}</p>
          {preview.steps && preview.steps.length > 0 && (
            <ol className="space-y-1 mb-3">
              {preview.steps.map((step, i) => (
                <li key={i} className="text-xs text-gray-600 flex gap-2">
                  <span className="text-gray-400 font-mono w-5 shrink-0">{step.step_number || i + 1}.</span>
                  <span>{step.description}</span>
                </li>
              ))}
            </ol>
          )}
          <div className="flex items-center gap-2">
            {isAdmin && (
              <button
                onClick={onExecute}
                disabled={isExecuting}
                className="px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                {isExecuting ? 'Executing…' : '✓ Confirm & Execute'}
              </button>
            )}
            {!isAdmin && (
              <span className="text-xs text-gray-500 italic">Admin access required to execute</span>
            )}
            <button
              onClick={() => { /* clear preview */ }}
              className="text-xs text-gray-400 hover:text-gray-600"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Execution result */}
      {result && (
        <div className={`px-4 py-3 border-t ${result.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
          {result.success ? (
            <div className="flex items-center gap-2">
              <span className="text-green-600">✓</span>
              <p className="text-sm font-medium text-green-800">
                Done — {result.steps_completed} step{result.steps_completed !== 1 ? 's' : ''} completed
              </p>
            </div>
          ) : (
            <div>
              <p className="text-sm font-medium text-red-700">Failed: {result.error}</p>
              {result.steps?.filter(s => s.success).length > 0 && (
                <p className="text-xs text-red-600 mt-0.5">
                  {result.steps.filter(s => s.success).length} step(s) completed before failure
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Snooze picker */}
      {showSnooze && (
        <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50 flex items-center gap-2">
          <span className="text-xs text-gray-500">Snooze for</span>
          <select
            value={snoozeDaysValue}
            onChange={e => onSnoozeDaysChange(Number(e.target.value))}
            className="px-2 py-1 text-xs border border-gray-300 rounded bg-white"
          >
            <option value={1}>1 day</option>
            <option value={2}>2 days</option>
            <option value={3}>3 days</option>
            <option value={5}>5 days</option>
            <option value={7}>1 week</option>
          </select>
          <button
            onClick={onSnooze}
            disabled={isSnoozing}
            className="px-2.5 py-1 text-xs font-medium bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50"
          >
            {isSnoozing ? 'Snoozing…' : 'Snooze'}
          </button>
        </div>
      )}
    </div>
  );
}
